import { Button, cn, Skeleton } from '@mevaser/ui'
import { type ColumnDef, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table'
import { ArrowDown, ArrowUp, ChevronsUpDown } from 'lucide-react'
import type * as React from 'react'
import { Fragment, useEffect, useRef } from 'react'

declare module '@tanstack/react-table' {
  interface ColumnMeta<TData, TValue> {
    /** Server-side sort key this column represents. Absence means unsortable. */
    sortKey?: string
  }
}

/**
 * The clickable column header, shared so a hand-rolled table (grouped rows,
 * aggregates the server doesn't compute) sorts and looks exactly like a
 * DataTable one. The caller owns the sort state; this only reports the click.
 */
export function SortHeaderButton({
  active,
  order,
  onClick,
  children,
}: {
  active: boolean
  order: 'asc' | 'desc'
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1 uppercase tracking-wide text-foreground-muted transition-colors hover:text-foreground"
    >
      {children}
      {active ? (
        order === 'asc' ? (
          <ArrowUp className="size-3" aria-hidden="true" />
        ) : (
          <ArrowDown className="size-3" aria-hidden="true" />
        )
      ) : (
        <ChevronsUpDown className="size-3 opacity-40" aria-hidden="true" />
      )}
    </button>
  )
}

export interface DataTableProps<T> {
  data: T[]
  columns: ColumnDef<T, unknown>[]
  isLoading?: boolean
  /** Rendered in place of the table body when there is nothing to show. */
  empty?: React.ReactNode
  onRowClick?: (row: T) => void
  /**
   * Content shown in a full-width row right under a data row — return null to
   * keep the row collapsed. The caller owns which rows are open.
   */
  renderExpanded?: (row: T) => React.ReactNode
  /**
   * Lazy-loads more rows as the admin scrolls, instead of paged Prev/Next
   * navigation — lists here can run long, and scrolling plus search is how
   * the back office finds things.
   */
  infinite?: {
    total: number
    hasMore: boolean
    isFetchingMore: boolean
    onLoadMore: () => void
  }
  /**
   * Opt-in column sorting. A column becomes clickable when it has
   * `meta: { sortKey }`; the table itself never sorts — it only reports
   * which key and direction to ask the server for next, same as pagination.
   */
  sort?: {
    column: string
    order: 'asc' | 'desc'
    onChange: (column: string, order: 'asc' | 'desc') => void
  }
  /** Compact rows for long lists; comfortable when scanning detail. */
  density?: 'compact' | 'comfortable'
  getRowId?: (row: T) => string
  selectedIds?: Set<string>
  onToggleRow?: (id: string) => void
  onToggleAll?: (ids: string[]) => void
}

/**
 * One table for every list in the back office.
 *
 * Pagination and sorting are server-side throughout: inventory will run to tens
 * of thousands of serialized units, and a client that fetches everything to
 * sort it locally stops working long before the business does.
 */
export function DataTable<T>({
  data,
  columns,
  isLoading,
  empty,
  onRowClick,
  renderExpanded,
  infinite,
  sort,
  density = 'compact',
  getRowId,
  selectedIds,
  onToggleRow,
  onToggleAll,
}: DataTableProps<T>) {
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting: true,
  })

  const rowPadding = density === 'compact' ? 'py-2' : 'py-3'
  const selectable = Boolean(selectedIds && onToggleRow && getRowId)
  const allIds = getRowId ? data.map(getRowId) : []
  const allSelected = selectable && allIds.length > 0 && allIds.every((id) => selectedIds?.has(id))

  return (
    <div className="overflow-hidden rounded-lg border border-border bg-surface">
      <div className="overflow-x-auto scrollbar-thin">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-border bg-surface-sunken/60">
              {selectable ? (
                <th className="w-10 px-3 py-2 text-start">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={() => onToggleAll?.(allIds)}
                    aria-label={allSelected ? 'Deselect all rows' : 'Select all rows'}
                    className="size-3.5 accent-[var(--primary)]"
                  />
                </th>
              ) : null}
              {table.getHeaderGroups().map((headerGroup) =>
                headerGroup.headers.map((header) => {
                  const sortKey = header.column.columnDef.meta?.sortKey
                  const sortable = Boolean(sort && sortKey)
                  const active = sortable && sort?.column === sortKey
                  return (
                    <th
                      key={header.id}
                      className="px-3 py-2 text-start text-2xs font-medium uppercase tracking-wide text-foreground-muted"
                      style={{ width: header.getSize() === 150 ? undefined : header.getSize() }}
                      aria-sort={
                        active ? (sort?.order === 'asc' ? 'ascending' : 'descending') : undefined
                      }
                    >
                      {header.isPlaceholder ? null : sortable && sortKey ? (
                        <SortHeaderButton
                          active={active}
                          order={sort?.order ?? 'asc'}
                          onClick={() =>
                            sort?.onChange(sortKey, active && sort.order === 'asc' ? 'desc' : 'asc')
                          }
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                        </SortHeaderButton>
                      ) : (
                        flexRender(header.column.columnDef.header, header.getContext())
                      )}
                    </th>
                  )
                }),
              )}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              Array.from({ length: 8 }, (_, rowIndex) => (
                <tr key={rowIndex} className="border-b border-border last:border-0">
                  {selectable ? <td className="px-3 py-2.5" /> : null}
                  {columns.map((_, columnIndex) => (
                    <td key={columnIndex} className="px-3 py-2.5">
                      <Skeleton
                        className="h-3.5"
                        style={{ width: columnIndex === 0 ? '60%' : '40%' }}
                      />
                    </td>
                  ))}
                </tr>
              ))
            ) : data.length === 0 ? (
              <tr>
                <td colSpan={columns.length + (selectable ? 1 : 0)}>{empty}</td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => {
                const id = getRowId ? getRowId(row.original) : String(row.id)
                const selected = selectedIds?.has(id)
                const expanded = renderExpanded?.(row.original)
                return (
                  <Fragment key={row.id}>
                    <tr
                      onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                      className={cn(
                        'border-b border-border transition-colors last:border-0',
                        onRowClick && 'cursor-pointer hover:bg-surface-sunken/60',
                        selected && 'bg-primary-subtle/40',
                      )}
                    >
                      {selectable ? (
                        <td className={cn('px-3', rowPadding)} onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={selected}
                            onChange={() => onToggleRow?.(id)}
                            aria-label="Select row"
                            className="size-3.5 accent-[var(--primary)]"
                          />
                        </td>
                      ) : null}
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id} className={cn('px-3', rowPadding)}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                    {expanded ? (
                      <tr className="border-b border-border bg-surface-sunken/40 last:border-0">
                        <td colSpan={columns.length + (selectable ? 1 : 0)} className="px-3 py-3">
                          {expanded}
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                )
              })
            )}
          </tbody>
        </table>
      </div>

      {infinite ? (
        <InfiniteScrollFooter
          total={infinite.total}
          loaded={data.length}
          hasMore={infinite.hasMore}
          isFetchingMore={infinite.isFetchingMore}
          onLoadMore={infinite.onLoadMore}
        />
      ) : null}
    </div>
  )
}

/**
 * Watches a sentinel near the bottom of a scrolled list and asks for more
 * rows before the admin actually reaches the end, so scrolling never stalls
 * waiting on a fetch. Exported so a hand-rolled table (grouped rows, its own
 * markup) gets the same footer and behavior as a DataTable one.
 */
export function InfiniteScrollFooter({
  total,
  loaded,
  hasMore,
  isFetchingMore,
  onLoadMore,
}: {
  total: number
  loaded: number
  hasMore: boolean
  isFetchingMore: boolean
  onLoadMore: () => void
}) {
  const sentinelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!hasMore || isFetchingMore) return
    const node = sentinelRef.current
    if (!node) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) onLoadMore()
      },
      { rootMargin: '400px' },
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [hasMore, isFetchingMore, onLoadMore])

  if (total === 0) return null

  return (
    <div className="border-t border-border px-3 py-2">
      {hasMore ? <div ref={sentinelRef} aria-hidden="true" /> : null}
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs text-foreground-muted tabular">
          {isFetchingMore
            ? 'Loading more…'
            : hasMore
              ? `Showing ${loaded} of ${total}`
              : `All ${total} loaded`}
        </p>
        {hasMore ? (
          <Button variant="ghost" size="sm" onClick={onLoadMore} disabled={isFetchingMore}>
            Load more
          </Button>
        ) : null}
      </div>
    </div>
  )
}

/** Slides up when rows are selected, so bulk actions never sit unused on screen. */
export function BulkActionBar({
  count,
  onClear,
  children,
}: {
  count: number
  onClear: () => void
  children: React.ReactNode
}) {
  if (count === 0) return null

  return (
    <div className="sticky bottom-4 z-20 mx-auto flex w-fit items-center gap-3 rounded-xl border border-border bg-surface px-3 py-2 shadow-lg">
      <span className="text-sm font-medium tabular">{count} selected</span>
      <div className="h-4 w-px bg-border" />
      {children}
      <Button variant="ghost" size="sm" onClick={onClear}>
        Clear
      </Button>
    </div>
  )
}
